import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { WebSocketService, UserPublicInfo } from './websocket.service';
import { ConfigService } from './config.service';

export interface ChatMessage {
  id: string;
  roomId: string;
  userId: string;
  userInfo: UserPublicInfo;
  content: string;
  messageType: string;
  timestamp: Date;
  isOwn?: boolean;
}

export interface ChatRoom {
  id: string;
  name: string;
  type: 'proximity' | 'place' | 'private';
  participants: UserPublicInfo[];
  lastMessage?: ChatMessage;
  unreadCount: number;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private activeRoomsSubject = new BehaviorSubject<ChatRoom[]>([]);
  private currentUserId: string | null = null;

  public messages$ = this.messagesSubject.asObservable();
  public activeRooms$ = this.activeRoomsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private wsService: WebSocketService,
    private configService: ConfigService
  ) {
    this.initializeWebSocketSubscriptions();
  }

  private initializeWebSocketSubscriptions(): void {
    // Suscribirse a mensajes de chat
    this.wsService.chatMessages$.subscribe(message => {
      this.handleNewChatMessage(message.data);
    });

    // Suscribirse a usuarios que se unen
    this.wsService.messages$.subscribe(message => {
      if (message.type === 'UserJoinedChat') {
        this.handleUserJoined(message.data.room_id, message.data.user_info);
      } else if (message.type === 'UserLeftChat') {
        this.handleUserLeft(message.data.room_id, message.data.user_id);
      }
    });
  }

  setCurrentUser(userId: string): void {
    this.currentUserId = userId;
  }

  private handleNewChatMessage(data: any): void {
    const message: ChatMessage = {
      id: data.message_id,
      roomId: data.room_id,
      userId: data.user_id,
      userInfo: data.user_info,
      content: data.content,
      messageType: data.message_type,
      timestamp: new Date(data.timestamp),
      isOwn: this.currentUserId === data.user_id
    };

    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);

    // Actualizar last message en rooms
    this.updateRoomLastMessage(message.roomId, message);
  }

  private handleUserJoined(roomId: string, userInfo: UserPublicInfo): void {
    const rooms = this.activeRoomsSubject.value;
    const roomIndex = rooms.findIndex(r => r.id === roomId);
    
    if (roomIndex !== -1) {
      const updatedRoom = { ...rooms[roomIndex] };
      if (!updatedRoom.participants.some(p => p.id === userInfo.id)) {
        updatedRoom.participants.push(userInfo);
        rooms[roomIndex] = updatedRoom;
        this.activeRoomsSubject.next([...rooms]);
      }
    }
  }

  private handleUserLeft(roomId: string, userId: string): void {
    const rooms = this.activeRoomsSubject.value;
    const roomIndex = rooms.findIndex(r => r.id === roomId);
    
    if (roomIndex !== -1) {
      const updatedRoom = { ...rooms[roomIndex] };
      updatedRoom.participants = updatedRoom.participants.filter(p => p.id !== userId);
      rooms[roomIndex] = updatedRoom;
      this.activeRoomsSubject.next([...rooms]);
    }
  }

  private updateRoomLastMessage(roomId: string, message: ChatMessage): void {
    const rooms = this.activeRoomsSubject.value;
    const roomIndex = rooms.findIndex(r => r.id === roomId);
    
    if (roomIndex !== -1) {
      const updatedRoom = { ...rooms[roomIndex] };
      updatedRoom.lastMessage = message;
      if (!message.isOwn) {
        updatedRoom.unreadCount++;
      }
      rooms[roomIndex] = updatedRoom;
      this.activeRoomsSubject.next([...rooms]);
    }
  }

  // ==================== API METHODS ====================

  async joinProximityChat(): Promise<boolean> {
    try {
      // Generar un room_id de proximidad basado en ubicación
      // Por ahora usaremos un room_id genérico, pero deberia ser basado en lat/lng
      const proximityRoomId = 'proximity_general'; // TODO: usar lat_lng cuando esté disponible
      
      console.log('📡 Joining proximity chat room:', proximityRoomId);
      const success = this.wsService.joinChatRoom(proximityRoomId);
      
      if (success) {
        this.addRoom({
          id: proximityRoomId,
          name: 'Chat de Proximidad',
          type: 'proximity',
          participants: [],
          unreadCount: 0,
          isActive: true
        });
        console.log('✅ Successfully joined proximity chat');
      } else {
        console.error('❌ Failed to join proximity chat');
      }
      
      return success;
    } catch (error) {
      console.error('Error joining proximity chat:', error);
      return false;
    }
  }

  async sendMessage(roomId: string, content: string): Promise<boolean> {
    try {
      // ✅ Usar WebSocket en lugar de HTTP
      console.log('📤 Sending chat message via WebSocket:', { roomId, content });
      const success = this.wsService.sendChatMessage(roomId, content);
      
      if (!success) {
        console.error('Failed to send message via WebSocket');
      }
      
      return success;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  async loadRoomMessages(roomId: string, limit: number = 50): Promise<ChatMessage[]> {
    try {
      // TODO: Implementar carga de mensajes via WebSocket o mantener en memoria
      // Por ahora retornamos array vacío ya que los mensajes llegaran en tiempo real
      console.log('📜 Loading room messages for:', roomId, '(currently disabled - messages arrive via WebSocket)');
      
      // Los mensajes actuales se obtienen del BehaviorSubject
      const currentMessages = this.messagesSubject.value;
      return currentMessages.filter(msg => msg.roomId === roomId);
      
    } catch (error) {
      console.error('Error loading room messages:', error);
      return [];
    }
  }

  joinRoom(roomId: string): boolean {
    return this.wsService.joinChatRoom(roomId);
  }

  leaveRoom(roomId: string): boolean {
    const success = this.wsService.leaveChatRoom(roomId);
    if (success) {
      this.removeRoom(roomId);
    }
    return success;
  }

  private addRoom(room: ChatRoom): void {
    const rooms = this.activeRoomsSubject.value;
    if (!rooms.some(r => r.id === room.id)) {
      this.activeRoomsSubject.next([...rooms, room]);
    }
  }

  private removeRoom(roomId: string): void {
    const rooms = this.activeRoomsSubject.value;
    this.activeRoomsSubject.next(rooms.filter(r => r.id !== roomId));
  }

  markRoomAsRead(roomId: string): void {
    const rooms = this.activeRoomsSubject.value;
    const roomIndex = rooms.findIndex(r => r.id === roomId);
    
    if (roomIndex !== -1) {
      const updatedRoom = { ...rooms[roomIndex] };
      updatedRoom.unreadCount = 0;
      rooms[roomIndex] = updatedRoom;
      this.activeRoomsSubject.next([...rooms]);
    }
  }

  getMessagesForRoom(roomId: string): Observable<ChatMessage[]> {
    return this.messages$.pipe(
      map(messages => messages.filter(msg => msg.roomId === roomId))
    );
  }
}